import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Lock, AlertCircle } from "lucide-react";
import { api, euro, errMsg } from "@/lib/api";
import { Page, Spinner } from "@/components/Layout";

let stripePromise = null;
const getStripe = async () => {
  if (!stripePromise) {
    const { data } = await api.get("/pubblico/config");
    stripePromise = loadStripe(data.stripe_publishable_key);
  }
  return stripePromise;
};

const appearance = {
  theme: "stripe",
  variables: { colorPrimary: "#7E5BAF", colorBackground: "#FFFEFF", colorText: "#261E1B", borderRadius: "12px", fontFamily: "Plus Jakarta Sans, system-ui, sans-serif" },
};

const PayForm = ({ ordine, token }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errore, setErrore] = useState(ordine.stato === "fallito" ? ordine.ultimo_errore : null);
  const [busy, setBusy] = useState(false);

  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setErrore(null);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/ordine/${ordine.id}?t=${token}` },
    });
    if (error) setErrore(error.message || "Pagamento non riuscito. Riprova.");
    setBusy(false);
  };

  return (
    <form onSubmit={pay} className="space-y-6" data-testid="payment-form">
      {errore && (
        <div className="flex gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="payment-error">
          <AlertCircle size={18} className="shrink-0" />
          <span>{errore} Puoi riprovare con lo stesso o un altro metodo di pagamento.</span>
        </div>
      )}
      <PaymentElement options={{ layout: "tabs", defaultValues: { billingDetails: { email: ordine.acquirente_email, address: { country: "IT" } } } }} />
      <button className="btn-primary w-full" disabled={!stripe || busy} data-testid="paga-btn">
        <Lock size={16} /> {busy ? "Elaborazione…" : `Paga ${euro(ordine.totale_cent)}`}
      </button>
    </form>
  );
};

export const Riepilogo = ({ o }) => (
  <div className="card space-y-3" data-testid="order-summary">
    <p className="caption">Riepilogo</p>
    <p className="font-display text-2xl">{o.operatore.ragione_sociale}</p>
    <p className="text-sm text-[#786E65]">{o.prodotto.titolo}</p>
    <div className="flex justify-between text-sm border-t border-[#E3D9F0] pt-3"><span>{o.quantita} × {euro(o.importo_unit_cent)}</span><span>{euro(o.totale_cent)}</span></div>
    {o.regalo && <p className="text-sm">Regalo per <strong>{o.destinatario_nome}</strong>{o.data_consegna ? ` — consegna il ${new Date(o.data_consegna).toLocaleDateString("it-IT")}` : " — consegna immediata"}</p>}
    <div className="flex justify-between font-semibold border-t border-[#E3D9F0] pt-3"><span>Totale</span><span data-testid="summary-total">{euro(o.totale_cent)}</span></div>
  </div>
);

export default function Checkout() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t");
  const [ordine, setOrdine] = useState(null);
  const [err, setErr] = useState(null);
  const stripeP = useMemo(() => getStripe(), []);

  useEffect(() => {
    api.get(`/pubblico/ordini/${id}`, { params: { t: token } }).then((r) => setOrdine(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id, token]);

  if (err) return <Page><div className="card text-center" data-testid="checkout-error">{err}</div></Page>;
  if (!ordine) return <Spinner />;
  if (ordine.stato === "pagato") return <Page><div className="card text-center py-12">Ordine già pagato. <Link className="text-[#7E5BAF] underline" to={`/ordine/${id}?t=${token}`}>Vedi l'esito</Link></div></Page>;

  return (
    <Page className="max-w-5xl">
      <p className="caption">Console pagamenti</p>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 mb-8">Completa il pagamento</h1>
      <div className="grid md:grid-cols-5 gap-8">
        <div className="md:col-span-3 card">
          <Elements stripe={stripeP} options={{ clientSecret: ordine.client_secret, appearance, locale: "it" }}>
            <PayForm ordine={ordine} token={token} />
          </Elements>
          <p className="text-xs text-[#786E65] mt-4 flex items-center gap-1"><Lock size={12} /> Dati della carta gestiti da Stripe con 3D Secure. Ambiente di test: usa 4242 4242 4242 4242.</p>
        </div>
        <div className="md:col-span-2"><Riepilogo o={ordine} /></div>
      </div>
    </Page>
  );
}
